import React, { useRef, useEffect, useState, createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import store from 'utils/store';
import produce from 'utils/produce';
import { useRouter } from 'next/router';
import useModalContext from 'components/context/modal';
import { Button } from 'components/base';

import usersApi from '../api/users';
import AuthCookie from '../lib/authCookie';
import authApi from '../api/auth';
import ancestryAuth from '../lib/ancestryAuth';
import { mergeUserData } from '../lib/userData';

const DEFAULT_VALUE = {
  data: {},
  cards: null,
  version: 2,
};

/**
 * Simple validation
 *
 * The persisted store is only valid if it matches the current access token.
 * If the auth cookie is gone (accessToken == null) we must reject any cached
 * user data — otherwise stale `authentication_providers` leak into flows like
 * the Ancestry sign-in handler and trigger a phantom account-conflict dialog.
 *
 * @param {Object} value
 * @param {string|null} accessToken
 */
const isUserStoreValid = (value, accessToken) => {
  if (!value) {
    return false;
  }

  if (value.version !== DEFAULT_VALUE.version) {
    return false;
  }

  for (const key of ['id', 'access_token', 'auth_providers', 'country_alpha2']) {
    if (!value.data[key]) {
      return false;
    }
  }

  if (value.data.access_token !== accessToken) {
    return false;
  }

  return true;
};

const UserContext = createContext();

export const UserContextProvider = props => {
  const modalContext = useModalContext();
  const router = useRouter();

  const [accessToken, setAccessToken] = useState(props.accessToken);

  const userDataRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [userStore, setUserStore] = useState();

  const updateDetails = async (initialFetch = false) => {
    if (accessToken) {
      const { data, error } = await usersApi(accessToken).getDetails();

      if (error) {
        AuthCookie.destroy();
        setUserStore(DEFAULT_VALUE);
        setAccessToken(null);
      } else {
        setUserStore(val =>
          produce(val ?? DEFAULT_VALUE, draft => {
            draft.data = data;
          }),
        );

        if (initialFetch) {
          props.onUserSet(data);
        }
      }
    }
  };

  const updateCards = async (initialFetch = false) => {
    if (accessToken) {
      const { data, error } = await usersApi(accessToken).getSavedCards();

      if (!error) {
        // The store may still be unset when cards resolve before details
        // (parallel reload on a full page load with an invalid session cache).
        setUserStore(val =>
          produce(val ?? DEFAULT_VALUE, draft => {
            draft.cards = data;
          }),
        );
      }
    }
  };

  const updateAncestryToken = ancestryToken => {
    setUserStore(val =>
      produce(val, draft => {
        draft.data.ancestry_token = ancestryToken;
      }),
    );
  };

  useEffect(() => {
    userDataRef.current = userStore?.data || {};
  }, [userStore]);

  useEffect(() => {
    const value = store.session.getCompressedJSON(props.storageKey, DEFAULT_VALUE);

    if (isUserStoreValid(value, accessToken)) {
      setUserStore(value);
      setIsLoading(false);

      props.onUserSet(value.data);
    } else {
      store.session.remove(props.storageKey);

      if (accessToken) {
        (async () => {
          const { data, error } = await usersApi(accessToken).getDetails();

          if (error) {
            AuthCookie.destroy();
            setUserStore(DEFAULT_VALUE);
            setAccessToken(null);
          } else {
            setUserStore(
              produce(DEFAULT_VALUE, draft => {
                draft.data = data;
              }),
            );
            props.onUserSet(data);
          }
          setIsLoading(false);
        })();
      } else {
        setUserStore(DEFAULT_VALUE);
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isLoading && accessToken) {
      if (!isUserStoreValid(userStore, accessToken)) {
        updateDetails(true);
      }
    }
  }, [isLoading, accessToken, userStore]);

  useEffect(() => {
    if (!isLoading) {
      if (isUserStoreValid(userStore, accessToken)) {
        store.session.setCompressedJSON(props.storageKey, userStore, true);
      }
    }
  }, [userStore, accessToken, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      if (!userStore.cards) {
        updateCards();
      }
    }
  }, [isLoading]);

  const handleOnAccessTokenChange = (value = null) => {
    if (value) {
      AuthCookie.set(value);
    } else {
      AuthCookie.destroy();
    }

    setAccessToken(value);
  };

  //
  // ANCESTRY
  //
  useEffect(() => {
    if (!isLoading && props.ancestryCode) {
      window.history.replaceState('', '', window.location.pathname);

      // TODO
      const type = props.ancestryCode.includes("-") ? 'family-search' : 'ancestry';

      (async ({ label, providerId }) => {
        modalContext.spinner(<>Getting your {label} account...</>, {
          title: `Login with ${label}`,
        });

        const ancestryProvider = (userDataRef.current.authentication_providers || []).find(
          provider => provider.type === providerId,
        );

        if (accessToken && !ancestryProvider) {
          // User is signed in and has no ancestry provider, then try to add ancestry provider
          const { data, error } = await authApi().signInWithFamilyTreeProvider(type, props.ancestryCode, true);

          if (error) {
            modalContext.toast(`Problem with ${label} sign in. Please try it again.`, {
              type: 'warning',
            });
            props.onCustomEvent(`signin_${type}_error`);
          } else {
            const { error: addProviderError } = await usersApi(accessToken).addProvider(
              data.provider,
            );

            if (addProviderError) {
              props.onCustomEvent(`${type}_provider_conflict`);
              modalContext.show(
                <>
                  The {label} account you are trying to connect to {data.provider.email && <>(
                    <strong>{data.provider.email}</strong>)</>} is already used by another MyCanvas
                  account. Each {label} account can only be associated with one MyCanvas account.
                  <br />
                  <br />
                  To use this {label} account, follow these steps:
                  <br />
                  <br />
                  1) Sign out of this MyCanvas account{' '}
                  <a href="/user" className="text-link">
                    using this link
                  </a>
                  <br />
                  2) Sign in to MyCanvas using the Login with {label} button instead
                </>,
                {
                  title: 'Oops! Account already in use',
                  footer: ({ close }) => (
                    <Button type="primary" onClick={close}>
                      OK
                    </Button>
                  ),
                },
              );
            } else {
              props.onCustomEvent(`signin_${type}_success`);
              updateDetails();
            }
          }
        } else {
          // Sign In/Up with Ancestry
          const { data, error } = await authApi().signInWithFamilyTreeProvider(type, props.ancestryCode);

          if (data) {
            // When user is signed in and has ancestry provider, then check if user is the same
            if (ancestryProvider && data.id !== userDataRef.current.id) {
              const newAccountAncestryProvider = (data.authentication_providers || []).find(
                provider => provider.type === providerId,
              );
              props.onCustomEvent(`${type}_provider_conflict`);
              modalContext.show(
                <>
                  Each {label} account can only be associated with one MyCanvas account. You
                  typically connect to a different {label} account (
                  <strong>{ancestryProvider.email}</strong>) from this MyCanvas account. Now, you
                  are trying to connect to the {label} account (
                  <strong>{newAccountAncestryProvider.email}</strong>).
                  <br />
                  <br />
                  To use this {label} account, follow these steps:
                  <br />
                  <br />
                  1) Sign out of this MyCanvas account{' '}
                  <a href="/user" className="text-link">
                    using this link
                  </a>
                  <br />
                  2) Sign in to MyCanvas using the Login with {label} button instead
                </>,
                {
                  title: `Oops! You are trying to sign in with different ${label} account`,
                  footer: ({ close }) => (
                    <Button type="primary" onClick={close}>
                      OK
                    </Button>
                  ),
                },
              );
            } else {
              handleOnAccessTokenChange(data.access_token);
              setUserStore(
                produce(DEFAULT_VALUE, draft => {
                  draft.data = data;
                }),
              );
              props.onUserSet(data);
              props.onCustomEvent(`signin_${type}_success`);

              modalContext.toast(
                `You have been successfully logged in with your ${label} account`,
                {
                  type: 'success',
                },
              );
            }
          } else {
            console.error(error);
            // handleOnAccessTokenChange();
            // setUserStore(DEFAULT_VALUE);
            // store.session.remove(props.storageKey);
            props.onCustomEvent(`signin_${type}_error`);
            modalContext.toast(`Problem with ${label} sign in. Please try it again.`, {
              type: 'warning',
            });
          }
        }

        modalContext.hideSpinner();

        if (props.ancestryState) {
          try {
            const ancestryState = JSON.parse(props.ancestryState);
            if (ancestryState.url) {
              router.push(ancestryState.url);
            }
          } catch (e) { }
        }
      })(type === "ancestry" ? { label: "Ancestry", type, providerId: 6 } : { label: "FamilySearch", type, providerId: 7 });
    }
  }, [isLoading, props.ancestryCode, props.ancestryState]);

  return (
    <UserContext.Provider
      value={useMemo(
        () => ({
          isLoading,

          accessToken,

          ancestryAuth: ancestryAuth(isLoading ? {} : userStore.data, updateAncestryToken),

          setAccessToken: handleOnAccessTokenChange,

          data: isLoading ? {} : userStore.data,

          cards: isLoading ? [] : userStore.cards || [],

          // Background refresh: both fetches run in parallel and isLoading stays
          // false, so consumers keep the cached data (and saved cards) instead of
          // pausing queries and unmounting UI while the refresh is in flight.
          reload: () => Promise.all([updateDetails(), updateCards()]),

          updateCards,

          getShippingAddress(defaultShippingAddress) {
            if (isLoading) {
              return {};
            }

            if (!userStore.data.shipping_address?.country_code && defaultShippingAddress) {
              return {
                email: userStore.data.email,
                ...defaultShippingAddress,
              };
            }

            return {
              email: userStore.data.email,
              name: userStore.data.shipping_address.name,
              street: userStore.data.shipping_address.street,
              street2: userStore.data.shipping_address.street2,
              zip: userStore.data.shipping_address.zip,
              city: userStore.data.shipping_address.city,
              state: userStore.data.shipping_address.state,
              phone: userStore.data.shipping_address.phone,
              country: userStore.data.shipping_address.country,
              countryCode: userStore.data.shipping_address.country_alpha2,
            };
          },

          setData(data = {}) {
            setUserStore(val =>
              produce(val ?? DEFAULT_VALUE, draft => {
                draft.data = data;
              }),
            );
          },

          // Prefer over setData after an async call that changed only some fields.
          mergeData(patch = {}) {
            setUserStore(val => mergeUserData(val ?? DEFAULT_VALUE, patch));
          },

          clear() {
            setUserStore(DEFAULT_VALUE);
            store.session.remove(props.storageKey);
            props.onUserClear();
          },
        }),
        [isLoading, userStore, accessToken],
      )}
    >
      {props.children}
    </UserContext.Provider>
  );
};

UserContextProvider.propTypes = {
  storageKey: PropTypes.string,
  accessToken: PropTypes.string,
  ancestryCode: PropTypes.string,
  ancestryState: PropTypes.string,
  onUserSet: PropTypes.func,
  onUserClear: PropTypes.func,
  onCustomEvent: PropTypes.func,
  children: PropTypes.node,
};

UserContextProvider.defaultProps = {
  storageKey: 'user_next',
  onUserSet: () => { },
  onUserClear: () => { },
  onCustomEvent: () => { },
};

const useUserContext = () => {
  return useContext(UserContext);
};

export default useUserContext;
